import './styles/IOUserModal.scss';

import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CREATE_MUTATION from 'gql/mutations/InternationalOffice/addIOUser.gql';
import UPDATE_MUTATION from 'gql/mutations/InternationalOffice/updateIOUser.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { UserInformation } from './components/UserInformation';
import { ioUserValidationSchema } from './validation';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  user?: any;
  setDisabledCancel?: Function;
}

export function IOUser(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createIOUser] = useMutation(CREATE_MUTATION, { context: { clientName: 'federation' } });
  const [updateIOUser] = useMutation(UPDATE_MUTATION, { context: { clientName: 'federation' } });

  const initialValues = () => {
    return props.user
      ? {
          ...pick(props.user, [
            'id',
            'email',
            'firstName',
            'lastName',
            'homeUniversity',
            'courseOfStudies',
            'semester'
          ])
        }
      : {};
  };

  const isEmailTaken = async value => {
    const { data } = await Gateway.get(`/users/isEmailTaken?userEmail=${value}`);
    return data;
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    let emailTaken = false;
    if (props.user?.email !== values.email) {
      try {
        emailTaken = await isEmailTaken(values.email);
      } catch (err) {
        props.closeHandler();
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.users.alerts.error.checkingEmail())
          )
        );
      }
    }

    if (emailTaken) {
      setSubmitting(false);
      if (props.setDisabledCancel) {
        props.setDisabledCancel(false);
      }
      dispatch(AlertActions.error(t(translations.validation.emailTaken())));
      return;
    }

    const omitFields = ['id'];
    let saveBody: any = {
      ...omit(values, omitFields),
      semester: parseInt(values.semester, 10)
    };

    if (values.id) {
      // * UPDATE IO-USER
      try {
        await updateIOUser({
          variables: {
            id: parseInt(values.id),
            user: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetUsers']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.users.alerts.success.updatedUser())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.users.alerts.error.updatingUser())
          )
        );
      }
    } else {
      // * CREATE IO-USER
      try {
        await createIOUser({
          variables: { user: omitBy(saveBody, v => v === null || v === '') },
          refetchQueries: ['GetUsers']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.users.alerts.success.createdUser())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.users.alerts.error.creatingUser())
          )
        );
      }
    }

    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
    props.closeHandler();
  };

  const steps = ['User Creation'];

  const UserModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <UserInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="ioUserModal">
      <Formik
        isInitialValid={!!props.user}
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={ioUserValidationSchema(t)}
        validate={values => {
          return validateForm(values, ioUserValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm submitting={submitting} steps={steps} onCancel={props.onCancel} hideStepper>
          {activeStep => (
            <Grid container>
              <UserModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
